<template>
  <div
    class="tw-flex tw-w-full tw-flex-1 tw-cursor-pointer tw-items-center tw-justify-between tw-gap-3 tw-border-t tw-border-gray-200 tw-px-2 tw-py-6 hover:tw-bg-gray-50"
    @click="viewFileHandler"
  >
    <div class="tw-flex tw-w-1/2 tw-items-center tw-gap-x-3 tw-text-left">
      <FileIcon class="tw-hidden tw-h-5 tw-w-5 xl:tw-block" :class="IconColor" />
      <div class="tw-max-w-full">
        <p
          v-tooltip.right="'View File'"
          class="tw-cursor-pointer tw-truncate tw-font-outfit tw-font-medium tw-text-gray-700 hover:tw-underline"
        >
          {{ document.fileName }}
        </p>
        <p class="tw-text-sm tw-text-gray-600">
          {{ moment(document.createdAt).format('MMMM DD, YYYY') }}
        </p>
      </div>
    </div>
    <div class="tw-flex tw-flex-1 tw-flex-col tw-gap-y-2 tw-text-left">
      <p class="tw-text-gray-700">
        {{ displayActionType }}
      </p>
      <FilesStatusChip :status="chipStatus" :label="chipLabel" />
    </div>
    <div v-if="document.signedUrl" class="tw-flex tw-items-center tw-justify-center">
      <button
        v-tooltip.left="'Download'"
        class="tw-mx-auto tw-rounded-full tw-border tw-border-gray-200 tw-p-2"
        :class="[DownloadButtonBorderColor, headerButtonsHoverStyle]"
        type="button"
        @click.stop="event => downloadFileAndTrack(document.signedUrl, document.fileName, event)"
      >
        <ArrowDownload width="20" height="20" :class="ArrowDownloadIconColor" />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Filesystem, Directory } from '@capacitor/filesystem'
import {
  ActionRequestStatus,
  ActionRequestScreeningStatus,
  ActionRequestSubType,
  ActionRequestType,
} from '@torticity/types'
import moment from 'moment'

import ArrowDownload from '~icons/fluent/arrow-download-16-filled'
import DocumentIcon from '~icons/fluent/document-text-24-filled'
import ImageIcon from '~icons/fluent/image-24-filled'

const { getThemeTokenString } = useTheme()

const ArrowDownloadIconColor = getThemeTokenString('primary-text')
const DownloadButtonBorderColor = getThemeTokenString('primary-border')
const IconColor = getThemeTokenString('primary-icon')
const headerButtonsHoverStyle = getThemeTokenString(['hover-background', 'hover-border'])
const toast = useToast()

const FileIcon = computed(() => {
  if (props.document.fileType.includes('image')) {
    return ImageIcon
  }
  return DocumentIcon
})

const props = defineProps<{
  document: {
    id: string
    fileName: string
    fileType: string
    createdAt: string
    signedUrl: string
  }
  action: {
    id: string
    type: ActionRequestType
    subType: string
    status: ActionRequestStatus
  }
  chipLabels: Record<ActionRequestStatus, string>
  screeningStatus?: ActionRequestScreeningStatus
}>()

const { $analytics, $device } = useNuxtApp()

function viewFileHandler() {
  $analytics.trackElementClicked({
    text: props.action?.subType ?? props.action?.type,
    elementType: 'file open',
  })

  // TODO: What about non PDF documents? Direct to download?
  if (props.action.subType === ActionRequestSubType.DIRECT_DEPOSIT) {
    navigateTo(`/files/${props.action.id}/direct-deposit`)
  } else {
    navigateTo(`/files/${props.action.id}/file/${props.document.id}`)
  }
}

const displayActionType = computed(() => {
  return props.action.subType === ActionRequestSubType.DIRECT_DEPOSIT
    ? 'Secure direct deposit'
    : props.action?.subType ?? props.action?.type
})

const chipStatus = computed(() =>
  props.screeningStatus === undefined ? props.action.status : props.screeningStatus,
)

const chipLabel = computed(() => {
  if (props.screeningStatus === undefined) {
    return props.chipLabels[props.action.status]
  }
  return props.screeningStatus === ActionRequestScreeningStatus.APPROVED ? 'Approved' : 'Rejected'
})

async function downloadFileAndTrack(signedUrl: string, fileName?: string, event?: Event) {
  // Need to prevent default browser behavior for downloads
  event?.preventDefault()

  $analytics.trackElementClicked({
    text: props.action?.subType ?? props.action?.type,
    elementType: 'file download',
  })

  const actualFileName = fileName || 'download.pdf'
  const isNative = useIsNative()

  if (isNative) {
    await downloadWithCapacitor(signedUrl, actualFileName)
    return
  }

  if ($device.isMobile) {
    window.open(signedUrl, '_blank')
    return
  }

  fetch(signedUrl)
    .then(response => response.blob())
    .then(blob => {
      const blobUrl = window.URL.createObjectURL(blob)

      const link = document.createElement('a')
      link.href = blobUrl
      link.setAttribute('download', actualFileName)
      link.style.display = 'none'

      document.body.appendChild(link)
      link.click()

      window.URL.revokeObjectURL(blobUrl)
      document.body.removeChild(link)
    })
    .catch(error => {
      console.error('Download failed:', error)
    })
}

async function downloadWithCapacitor(url: string, fileName: string) {
  try {
    const result = await Filesystem.downloadFile({
      url,
      path: fileName,
      directory: Directory.Documents,
      progress: true, // Enable progress events
    })

    toast.add({
      severity: 'success',
      summary: 'Download Complete',
      detail: `File saved: ${fileName}`,
      life: 3000,
    })

    return result.path
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Download Failed',
      detail: 'Unable to download file. Please try again.',
      life: 3000,
    })
  }
}
</script>
